import React from 'react';
import { usePagination } from 'hooks/use-table-pagination.hook';
import { TableUtils } from 'pages/private/components/table-utils';
import { OrganisationSelect } from 'pages/private/components/organisation-select';
import { useRaptorArtifactPermissionContext } from 'contexts/permissions/raptor-artifact-permission.context';
import { ARTIFACT_UPLOAD_FILE_TYPE_LABELS, ARTIFACT_UPLOAD_FILE_TYPES, UploadFileDto } from 'dto/upload-file.dto';
import { useUploadFilesStore } from 'store';
import { AdvanceTable, IconTextButton, notification, showConfirmDialog, Typography } from 'ui-lib';
import { downloadFileFromUrl, getErrorMessage } from 'common-utils';
import useAccountSelector from '../hooks/use-account-selector';
import { useRaptorArtifactNavigator } from '../hooks/use-raptor-artifact-navigator';
import css from './artifacts.css';

export const ArtifactListingPage = () => {
    const uploadFilesStore = useUploadFilesStore();
    const permissionContext = useRaptorArtifactPermissionContext();
    const accountSelector = useAccountSelector();
    const navigation = useRaptorArtifactNavigator();
    const selectedAccount = accountSelector.selectedAccount;

    const { pagination, data, loading, refresh, onTableChange } = usePagination({
        pageSize: 100,
        fetchData: async (params) => {
            if (!selectedAccount) {
                return {
                    data: [],
                };
            }
            const { pageSize = 100, page = 1 } = params;
            const offset = (page - 1) * pageSize;
            const result = await uploadFilesStore.getArtifacts(selectedAccount.id, {
                offset,
                limit: pageSize,
            });
            return {
                data: result,
            };
        },
        enableShowTotal: true,
        cacheKey: `artifacts-table`,
        mode: 'offset',
    });

    React.useEffect(() => {
        refresh();
    }, [selectedAccount]);

    const onAdd = () => {
        if (!selectedAccount) {
            return notification.warning({
                message: 'Warning',
                description: 'Please select an organisation before uploading artifacts.',
            });
        }
        navigation.toArtifactUploadPage({ accountUuid: selectedAccount.uuid });
    };

    const onDelete = (record: UploadFileDto) => {
        showConfirmDialog({
            title: `Delete Artifact ${record.fileName}`,
            content: <Typography.Text>Are you sure you want to delete this artifact?</Typography.Text>,
            okText: 'OK',
            onOk: async () => {
                try {
                    const deleted = await uploadFilesStore.deleteUploadedFile(selectedAccount.id, record.uuid);
                    if (!deleted) {
                        return notification.error({
                            message: `Failed to delete artifact ${record.fileName}`,
                        });
                    }
                    notification.success({
                        message: `Artifact ${record.fileName} deleted successfully`,
                    });
                    refresh();
                } catch (err: any) {
                    notification.error({
                        message: `Failed to delete artifact ${record.fileName}`,
                        description: getErrorMessage(err),
                    });
                }
            },
        });
    };

    return (
        <AdvanceTable
            title='Artifacts'
            loadingState={loading}
            toolbar={{
                extraItems: accountSelector.isDPUAdmin
                    ? [
                          <OrganisationSelect
                              key='Organisation'
                              placeholder='Organisation'
                              disabled={loading}
                              treeDataSource={accountSelector.dataSource}
                              filterTreeNode={(input, treeNode) => {
                                  return treeNode.label?.includes(input);
                              }}
                              expandAll={true}
                              onChange={accountSelector.onChange}
                              value={selectedAccount?.id}
                          />,
                      ]
                    : [],
                onReload: refresh,
                onAdd: permissionContext.canModify ? onAdd : undefined,
            }}
            table={{
                rowKey: 'uuid',
                pagination,
                onTableChange,
                columns: [
                    TableUtils.createBasicFilterColumn({ key: 'fileName', title: 'File Name' }),
                    TableUtils.createBasicEnumFilterColumn({
                        key: 'type',
                        title: 'Type',
                        filters: ARTIFACT_UPLOAD_FILE_TYPES.map((type) => ({
                            value: type,
                            text: ARTIFACT_UPLOAD_FILE_TYPE_LABELS[type],
                        })),
                        columnExtraOptions: {
                            render: (text) => ARTIFACT_UPLOAD_FILE_TYPE_LABELS[text] ?? text,
                        },
                    }),
                    TableUtils.createTruncatedTextColumn({ key: 'description', title: 'Description' }),
                    TableUtils.createBasicFilterTimeColumn({ key: 'createdAt', title: 'Created At' }),
                    TableUtils.createExtraColumn({
                        key: 'actions',
                        title: 'Actions',
                        render: (_, record: UploadFileDto) => (
                            <div className={css['table-actions']}>
                                <IconTextButton
                                    key='download'
                                    type='link'
                                    label='Download'
                                    disabled={!record.awsArn}
                                    onClick={() => downloadFileFromUrl(record.awsArn, record.fileName)}
                                />
                                {permissionContext.canModifyArtifact(record) && (
                                    <IconTextButton
                                        key='delete'
                                        type='link'
                                        label='Delete'
                                        onClick={() => onDelete(record)}
                                    />
                                )}
                            </div>
                        ),
                    }),
                ],
                dataSource: data,
            }}
        />
    );
};
